import ApiHost from '@services/api-config';
import Axios from '@utils/request';
import { uuid } from '@utils/string-utils';
import { sendEvent } from '@core/track';

interface AliOssAccessParams {
	fileName: string;
}
export enum UploadMethod {
	put = 'put',
	multipartUpload = 'multipartUpload',
}

interface UploadParams {
	file: any;
	type: string;
	// params: AliOssAccessParams;
	uploadMethod: UploadMethod;
	quality?: number;
	percentCb?: Function;
}

interface AliOssToken {
	accessKeyId: string;
	accessKeySecret: string;
	filePath: string;
	securityToken: string;
	ossUrl: string;
	bucket?: string;
	endpoint?: string;
}

class UploadWithAliOss {
	public async getAccess(param: AliOssAccessParams): Promise<AliOssToken | null> {
		const res: RequestResponse<AliOssToken> = await Axios.post(`${ApiHost.airGateway}/air-tr-api/api/tr/oss`, param);
		if (res.code === '0') {
			return res.data;
		} else {
			console.log('获取ali-oss 权限失败');
			return null;
		}
	}
	public async upload(params: UploadParams): Promise<string | null> {
		const { file, uploadMethod, percentCb, quality, type } = params;
		// check type size or other
		const fileName = file.name;
		// 兼容文件名
		const fileType = fileName.substring(fileName.lastIndexOf('.') + 1);
		// 课件名称
		const slideHash = uuid();

		const aliOssToken: AliOssToken | null = await this.getAccess({ fileName: slideHash + '.' + fileType });
		if (!aliOssToken) return null;
		const { accessKeyId, accessKeySecret, securityToken, filePath, ossUrl, bucket, endpoint } = aliOssToken;
		const OSS_OPTION: any = {
			accessKeyId,
			accessKeySecret,
			bucket: bucket,
			stsToken: securityToken,
			secure: true
		};
		if (endpoint) {
			// 传输加速，自定义域名需要设置cname=true
			OSS_OPTION.endpoint = endpoint;
			OSS_OPTION.cname = true;
		}
		const client = window.OSS(OSS_OPTION);
		const blobInfo = file && file.blob ? file.blob() : file;
		const beginTime = Date.now();
		try {
			sendEvent({ eventId: 'ibu_teaAndClassroom_upLoadFile', eventParam: { ossUrl } });
			await client[uploadMethod](filePath, blobInfo, { progress: percentCb });
			// 成功日志
			const duration = (Date.now() - beginTime) / 1000;
			sendEvent({
				eventId: 'ibu_teaAndClassroom_upLoadFile_success',
				eventParam: { success: 1, ossUrl, fileName, fileType, fileSize: file.size, duration, }
			});
		} catch (error) {
			const duration = (Date.now() - beginTime) / 1000;
			// 失败日志
			sendEvent({
				eventId: 'ibu_teaAndClassroom_upLoadFile_failed',
				eventParam: { success: 0, ossUrl, fileName, fileType, fileSize: file.size, duration, errMsg: error?.message, errCode: error?.code, error }
			});
			return null;
		}
		if (!quality || ['jpg', 'jpeg', 'png', 'gif'].indexOf(type) === -1) {
			return ossUrl;
		}

		const res: any = await Axios.get(
			`${filePath}?x-oss-process=image${type !== 'gif' ? `/quality,Q_${quality}/` : '/'}resize,m_lfit,w_1200,h_675&tuneStep=${Date.now()}`
		)
		if (!res.blob()) {
			return ossUrl;
		}
		await client.put(filePath, res.blob())
		return ossUrl;
	}
}

/**
 * 文件上传AliOss服务
 * @param {Object} params
 * @param {File} params.file 文件|图片
 * @param {string} params.type jpg|jpeg|png|gif
 * @param {AliOssAccessParams} params.params 获取OSS权限的参数
 * @param {UploadMethod} params.uploadMethod 上传方式 put|multipartUpload
 * @param {number} params.quality 质量压缩
 * @param {Function} params.percentCb 上传方式 为 multipartUpload 进度回调 percentage 0~1： 0%~100%
 * @returns {Promise<string|null>} Promise<string|null>
 * @example uploadWithAliOss({ file, type, params: { fileName: 'xxx.png' }, uploadMethod: UploadMethod.multipartUpload, percentCb: percent => console.log(`进度${percent * 100}%`)});
 */
export const uploadWithAliOss: (params: UploadParams) => Promise<string | null> = (params) => {
	return new UploadWithAliOss().upload(params);
}
